import { sum, uniqueBy } from 'lib/array/arrayUtils'
import { LEBT } from 'typings/business-traveller'

type TravellerName = {
  firstName: string
  lastName: string
}

// Generic type for traveller data that works with both LEBT and App types
type TravellerData = {
  employee?: TravellerName
  guestTravellerData?: TravellerName
}

// Generic helper function that extracts traveller names from any traveller array
function extractTravellerNames(travellers: Array<TravellerData>) {
  const travellerNames: Array<TravellerName> = []
  let guestCount = 0

  travellers.forEach(traveller => {
    const guestData = traveller.guestTravellerData
    if (guestData?.firstName && guestData?.lastName) {
      travellerNames.push({
        firstName: guestData.firstName,
        lastName: guestData.lastName
      })
    } else if (traveller.employee?.firstName && traveller.employee?.lastName) {
      travellerNames.push({
        firstName: traveller.employee.firstName,
        lastName: traveller.employee.lastName
      })
    } else {
      guestCount++
    }
  })

  return {
    travellers: uniqueBy(travellerNames, t => `${t.firstName}|${t.lastName}`),
    guestCount,
  }
}

export function getUniqueTravellerNames(orderItems: Array<LEBT.OrderItem>) {
  const filteredOrderItems = orderItems.filter(item =>
    item.type !== 'service_fee' &&
    item.type !== 'insurance' &&
    item.type !== 'business_credit'
  )

  const travellers = filteredOrderItems.flatMap(item => item.OrderItemTraveller || [])
  return extractTravellerNames(travellers)
}

export function getUniqueBedbankTravellerNames(item: App.OrderBedbankItem) {
  const travellers = item.orderItemTraveller || []
  return extractTravellerNames(travellers)
}

export function getUniqueAccommodationTravellerNames(item: App.OrderItem) {
  const travellers = item.orderItemTraveller || []
  return extractTravellerNames(travellers)
}

function getAllTravellersFromAppOrder(order: App.Order): Array<App.BusinessTravellerOrderItemTraveller> {
  return [
    ...(order.items || []),
    ...(order.bedbankItems || []),
    ...(order.flightItems || []),
    ...(order.carHireItems || []),
    ...(order.giftCardItems || []),
    ...(order.customOfferItems || []),
  ].flatMap(item => item.orderItemTraveller || [])
}

export function getUniqueTravellerNamesFromAppOrder(order: App.Order) {
  return extractTravellerNames(getAllTravellersFromAppOrder(order))
}

export function getUniqueEmployeeTravellers(order: App.Order): Array<App.BusinessTravellerOrderItemTraveller> {
  return getAllTravellersFromAppOrder(order)
    .reduce<Array<App.BusinessTravellerOrderItemTraveller>>((unique, t) => {
      if (t.employeeId && !unique.some(u => u.employeeId === t.employeeId)) {
        unique.push(t)
      }
      return unique
    }, [])
}

export function getIsBookingManager(order: LEBT.Order | App.Order, employeeId: string | undefined) {
  return Boolean(!order.employeeId || (employeeId && order.employeeId === employeeId))
}

export function getCustomOfferFlightDepartureDate(item: LEBT.OrderItem): string | undefined {
  if (item.type !== 'custom_offer') {
    return undefined
  }

  const customOfferData = item.data as LEBT.CustomOfferOrderItemData
  if (customOfferData.type !== 'flight' || !customOfferData.flightLegs) {
    return undefined
  }

  const firstLeg = customOfferData.flightLegs[0]
  const firstSegment = firstLeg?.segments[0]

  return firstSegment?.departureDate
}

export function getCustomOfferFlightArrivalDate(item: LEBT.OrderItem): string | undefined {
  if (item.type !== 'custom_offer') {
    return undefined
  }

  const customOfferData = item.data as LEBT.CustomOfferOrderItemData
  if (customOfferData.type !== 'flight' || !customOfferData.flightLegs) {
    return undefined
  }

  const lastLeg = customOfferData.flightLegs.at(-1)
  const lastSegment = lastLeg?.segments.at(-1)

  return lastSegment?.arrivalDate
}

export function getCustomOfferDisplayStartDate(item: LEBT.OrderItem): string | undefined {
  const customOfferData = item.data as LEBT.CustomOfferOrderItemData

  if (customOfferData.type === 'flight') {
    const flightDepartureDate = getCustomOfferFlightDepartureDate(item)
    if (flightDepartureDate) {
      return flightDepartureDate
    }
  }

  return customOfferData.startDate
}

export function isOneWayFlight(item: LEBT.OrderItem): boolean {
  if (item.type !== 'custom_offer') {
    return false
  }

  const customOfferData = item.data as LEBT.CustomOfferOrderItemData
  if (customOfferData.type !== 'flight' || !customOfferData.flightLegs) {
    return false
  }

  return customOfferData.flightLegs.length === 1
}

export function getCustomOfferDisplayEndDate(item: LEBT.OrderItem): string | undefined {
  const customOfferData = item.data as LEBT.CustomOfferOrderItemData

  if (customOfferData.type === 'flight') {
    if (isOneWayFlight(item)) {
      return undefined
    }

    const flightArrivalDate = getCustomOfferFlightArrivalDate(item)
    if (flightArrivalDate) {
      return flightArrivalDate
    }
  }

  return customOfferData.endDate
}

export const CREDIT_STATUS = {
  PENDING: 'pending',
  APPLIED: 'applied',
  CANCELLED: 'cancelled',
}

export function getOrderTotalBusinessCredits(order: App.Order): number {
  return sum(
    getAllTravellersFromAppOrder(order),
    t => t.credits ?? 0,
  )
}

export function getOrderCancelledBusinessCredits(order: App.Order): number {
  return sum(
    getAllTravellersFromAppOrder(order).filter(t => t.creditStatus === CREDIT_STATUS.CANCELLED),
    t => t.credits ?? 0,
  )
}
